"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { BadgeCheck, Gavel, Gem, Images, Import, Plus, Radio, Sparkles, WandSparkles, Wallet } from "lucide-react";
import { useCreateCollectionModalStore } from "@/store/useCreateCollectionModalStore";
import { Popover } from "./ui";
import "./fab.css";

/**
 * THE CREATE BUTTON.
 *
 * WHERE IT SITS. Bottom-inline-end on desktop, 24px clear of both edges. On a
 * phone that corner belongs to the tab bar, so it moves directly above it and
 * becomes a disc. Putting it inside the tab bar as a sixth item was the
 * alternative and it is worse: creating is not a destination like the other
 * five, and a six-item bar at 375px gives every item 58px.
 *
 * WHAT IT DOES ABOUT COVERING THE PAGE — the real problem with any floating
 * button, and the reason this one has behaviour rather than just a position:
 *
 *   1. It HIDES over the footer. An IntersectionObserver watches the footer;
 *      while any of it is on screen the button drops away. The footer already
 *      lists "create a drop", "create a gallery" and "add funds" as plain
 *      links, so nothing is lost at the one place it would otherwise sit on
 *      top of a column of links.
 *
 *   2. It COLLAPSES while you scroll down and expands again when you stop or
 *      scroll back up. Reading is when it is most in the way and least wanted;
 *      pausing is when you might use it. On desktop that is pill → disc, which
 *      takes it from 132px wide to 46px.
 *
 * Both run off one passive scroll listener and one observer. No layout is read
 * during the scroll handler — only `window.scrollY`, which does not force
 * reflow — so this cannot cause scroll jank.
 *
 * THE ACTION LIST. This menu shipped with four of the eight things the product
 * can actually create — AI minting, collections, importing a deployed
 * contract, auctions and the verification request were all missing, and three
 * of those have no other entry point anywhere in the chrome. The list below is
 * the full set, grouped by what you are actually doing, with this design
 * system's icons; nothing about the previous menu's appearance came with it.
 */

export default function CreateFab(t0) {
  const $ = _c(40);
  const {
    lang
  } = t0;
  const isFa = lang === "fa";
  let t1;
  if ($[0] !== isFa) {
    t1 = (fa, en) => isFa ? fa : en;
    $[0] = isFa;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t = t1;
  let t2;
  if ($[2] !== lang) {
    t2 = p => `/${lang}${p}`;
    $[2] = lang;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const href = t2;
  const openCollectionModal = useCreateCollectionModalStore(_temp);
  const [hidden, setHidden] = useState(false);
  const anchor = useRef(null);
  const lastY = useRef(0);
  const compact = useRef(false);
  const idle = useRef(undefined);
  let t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const footer = document.querySelector(".v2-footer");
      if (!footer) {
        return;
      }
      const io = new IntersectionObserver(t5 => {
        const [entry] = t5;
        return setHidden(entry.isIntersecting);
      }, {
        threshold: 0
      });
      io.observe(footer);
      return () => io.disconnect();
    };
    t4 = [];
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      lastY.current = window.scrollY;
      let frame = 0;
      let pendingY = lastY.current;
      const applyCompact = next => {
        if (compact.current === next) {
          return;
        }
        compact.current = next;
        anchor.current?.toggleAttribute("data-compact", next);
      };
      const paint = () => {
        frame = 0;
        const y = pendingY;
        const down = y > lastY.current + 4;
        const up = y < lastY.current - 4;
        if (down && y > 240) {
          applyCompact(true);
        } else {
          if (up) {
            applyCompact(false);
          }
        }
        if (down || up) {
          lastY.current = y;
        }
      };
      const onScroll = () => {
        pendingY = window.scrollY;
        if (!frame) {
          frame = window.requestAnimationFrame(paint);
        }
        window.clearTimeout(idle.current);
        idle.current = window.setTimeout(() => applyCompact(false), 900);
      };
      window.addEventListener("scroll", onScroll, {
        passive: true
      });
      return () => {
        window.removeEventListener("scroll", onScroll);
        window.clearTimeout(idle.current);
        if (frame) {
          window.cancelAnimationFrame(frame);
        }
      };
    };
    t6 = [];
    $[6] = t5;
    $[7] = t6;
  } else {
    t5 = $[6];
    t6 = $[7];
  }
  useEffect(t5, t6);
  let T0;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t7;
  let t8;
  let t9;
  if ($[8] !== hidden || $[9] !== href || $[10] !== isFa || $[11] !== openCollectionModal || $[12] !== t) {
    const groups = [{
      title: t("\u0628\u0633\u0627\u0632\u06CC\u062F", "Make"),
      items: [{
        key: "nft",
        href: "/create-nft",
        Icon: Sparkles,
        fa: ["\u0633\u0627\u062E\u062A \u0627\u062B\u0631", "\u06CC\u06A9 \u0627\u0646\u200C\u0627\u0641\u200C\u062A\u06CC \u062A\u0627\u0632\u0647 \u0636\u0631\u0628 \u06A9\u0646\u06CC\u062F"],
        en: ["Create artwork", "Mint a new NFT"]
      }, {
        key: "ai",
        href: "/create-nft/ai",
        Icon: WandSparkles,
        fa: ["\u0633\u0627\u062E\u062A \u0627\u062B\u0631 \u0628\u0627 \u0647\u0648\u0634 \u0645\u0635\u0646\u0648\u0639\u06CC", "\u062C\u0632\u0626\u06CC\u0627\u062A \u0631\u0627 \u0647\u0648\u0634 \u0645\u0635\u0646\u0648\u0639\u06CC \u0645\u06CC\u200C\u0646\u0648\u06CC\u0633\u062F"],
        en: ["Create with AI", "Let AI write the details"]
      }, {
        key: "collection",
        action: openCollectionModal,
        Icon: Gem,
        fa: ["\u0633\u0627\u062E\u062A \u06A9\u0627\u0644\u06A9\u0634\u0646", "\u0645\u062C\u0645\u0648\u0639\u0647\u200C\u0627\u06CC \u0628\u0631\u0627\u06CC \u0622\u062B\u0627\u0631\u062A\u0627\u0646"],
        en: ["Create a collection", "A set to mint into"]
      }, {
        key: "import",
        href: "/collection/import",
        Icon: Import,
        fa: ["\u0627\u06CC\u0646\u062F\u06A9\u0633 \u06A9\u0627\u0644\u06A9\u0634\u0646 \u062E\u0627\u0631\u062C\u06CC", "\u0642\u0631\u0627\u0631\u062F\u0627\u062F \u0645\u0633\u062A\u0642\u0631\u0634\u062F\u0647\u200C\u06CC \u062E\u0648\u062F \u0631\u0627 \u0628\u06CC\u0627\u0648\u0631\u06CC\u062F"],
        en: ["Import a collection", "Index a contract you deployed"]
      }]
    }, {
      title: t("\u0639\u0631\u0636\u0647 \u06A9\u0646\u06CC\u062F", "Sell"),
      items: [{
        key: "auction",
        href: "/auctions/create",
        Icon: Gavel,
        fa: ["\u0633\u0627\u062E\u062A \u0645\u0632\u0627\u06CC\u062F\u0647", "\u0641\u0631\u0648\u0634 \u0631\u0642\u0627\u0628\u062A\u06CC \u06CC\u06A9 \u0627\u062B\u0631 \u06CC\u0627 \u06A9\u0627\u0644\u06A9\u0634\u0646"],
        en: ["Create an auction", "A competitive sale"]
      }, {
        key: "drop",
        href: "/drops/create",
        Icon: Radio,
        fa: ["\u0633\u0627\u062E\u062A \u062F\u0631\u0627\u067E", "\u0639\u0631\u0636\u0647\u200C\u06CC \u0632\u0645\u0627\u0646\u200C\u0628\u0646\u062F\u06CC\u200C\u0634\u062F\u0647"],
        en: ["Create a drop", "A timed release"]
      }, {
        key: "gallery",
        href: "/galleries/create",
        Icon: Images,
        fa: ["\u0633\u0627\u062E\u062A \u06AF\u0627\u0644\u0631\u06CC", "\u0622\u062B\u0627\u0631 \u0631\u0627 \u06A9\u0646\u0627\u0631 \u0647\u0645 \u0628\u06AF\u0630\u0627\u0631\u06CC\u062F"],
        en: ["Create a gallery", "Curate work together"]
      }]
    }, {
      title: t("\u062D\u0633\u0627\u0628", "Account"),
      items: [{
        key: "deposit",
        href: "/wallet",
        Icon: Wallet,
        fa: ["\u0627\u0641\u0632\u0627\u06CC\u0634 \u0645\u0648\u062C\u0648\u062F\u06CC", "\u0628\u0627 \u0631\u06CC\u0627\u0644\u060C \u0628\u062F\u0648\u0646 \u0635\u0631\u0627\u0641\u06CC"],
        en: ["Add funds", "In rial, no exchange"]
      }, {
        key: "verify",
        href: "/settings#blue-tick",
        Icon: BadgeCheck,
        fa: ["\u062F\u0631\u062E\u0648\u0627\u0633\u062A \u062A\u06CC\u06A9 \u0622\u0628\u06CC", "\u0646\u0634\u0627\u0646 \u0647\u0646\u0631\u0645\u0646\u062F \u062A\u0623\u06CC\u06CC\u062F\u0634\u062F\u0647"],
        en: ["Request verification", "The verified-artist badge"]
      }]
    }];
    t13 = "v2-fab-anchor";
    t14 = anchor;
    t15 = hidden || undefined;
    T0 = Popover;
    t7 = "end";
    t8 = "top";
    t9 = "v2-create-menu";
    if ($[23] !== t) {
      t10 = t("\u0633\u0627\u062E\u062A", "Create");
      $[23] = t;
      $[24] = t10;
    } else {
      t10 = $[24];
    }
    if ($[25] !== t) {
      t11 = t16 => {
        const {
          open,
          toggle,
          id
        } = t16;
        return <button type="button" className="v2-fab v2-focus" onClick={toggle} aria-expanded={open} aria-controls={id} aria-label={t("\u0633\u0627\u062E\u062A", "Create")} data-open={open || undefined}><Plus aria-hidden={true} /><span>{t("\u0633\u0627\u062E\u062A", "Create")}</span></button>;
      };
      $[25] = t;
      $[26] = t11;
    } else {
      t11 = $[26];
    }
    t12 = groups.map((group, gi) => <div key={group.title}>{gi > 0 ? <div className="v2-pop-sep" /> : null}<div className="v2-pop-title">{group.title}</div>{group.items.map(t16 => {
        const {
          key,
          href: p_0,
          action,
          Icon,
          fa: fa_0,
          en: en_0
        } = t16;
        const [title, hint] = isFa ? fa_0 : en_0;
        const body = <><Icon aria-hidden={true} /><span>{title}<small>{hint}</small></span></>;
        return action ? <button className="v2-pop-item" key={key} type="button" role="menuitem" onClick={action}>{body}</button> : <Link className="v2-pop-item" href={href(p_0)} key={key} role="menuitem">{body}</Link>;
      })}</div>);
    $[8] = hidden;
    $[9] = href;
    $[10] = isFa;
    $[11] = openCollectionModal;
    $[12] = t;
    $[13] = T0;
    $[14] = t10;
    $[15] = t11;
    $[16] = t12;
    $[17] = t13;
    $[18] = t14;
    $[19] = t15;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    T0 = $[13];
    t10 = $[14];
    t11 = $[15];
    t12 = $[16];
    t13 = $[17];
    t14 = $[18];
    t15 = $[19];
    t7 = $[20];
    t8 = $[21];
    t9 = $[22];
  }
  let t16;
  if ($[27] !== T0 || $[28] !== t10 || $[29] !== t11 || $[30] !== t12 || $[31] !== t7 || $[32] !== t8 || $[33] !== t9) {
    t16 = <T0 align={t7} side={t8} className={t9} label={t10} trigger={t11}>{t12}</T0>;
    $[27] = T0;
    $[28] = t10;
    $[29] = t11;
    $[30] = t12;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t13 || $[36] !== t14 || $[37] !== t15 || $[38] !== t16) {
    t17 = <div className={t13} ref={t14} data-hidden={t15}>{t16}</div>;
    $[35] = t13;
    $[36] = t14;
    $[37] = t15;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  return t17;
}
function _temp(s) {
  return s.openModal;
}